import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiSearch } from 'react-icons/fi';
import { methodClass } from './IntegrationDetailBody';
import './apiintegrations.css';

// What a platform reviewer sees about an API Integration before approving it for
// Global (Review Queue): where it calls, how it signs in, and every endpoint -
// what it does, what it sends, what comes back, and which ones change data.

export interface ReviewParam {
  name?: string; location?: string; type?: string; required?: boolean; description?: string | null; enumValues?: string[];
}
export interface ReviewEndpoint {
  name?: string; httpMethod?: string; pathTemplate?: string; summary?: string | null; description?: string | null;
  returnsSummary?: string | null; bodyFormat?: string | null; params?: ReviewParam[];
}
export interface ReviewIntegration {
  baseUrl?: string | null; authType?: string | null; authLocation?: string | null; authParamName?: string | null;
  installerKeyScope?: string | null; endpoints?: ReviewEndpoint[];
}

type Kind = 'read' | 'change' | 'delete';
type Filter = 'all' | Kind;

const FOLDED = 10;
const LOCATION_LABELS: Record<string, string> = {
  path: 'URL path', query: 'Query string', header: 'Header', body: 'Body', raw_body: 'Whole body',
};

function kindOf(method?: string): Kind {
  const m = (method || '').toUpperCase();
  if (m === 'DELETE') return 'delete';
  return m === 'GET' || m === 'HEAD' || m === 'OPTIONS' ? 'read' : 'change';
}

function hostOf(url?: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const EndpointRow: React.FC<{ endpoint: ReviewEndpoint }> = ({ endpoint }) => {
  const [open, setOpen] = useState(false);
  const params = endpoint.params ?? [];
  const kind = kindOf(endpoint.httpMethod);
  const description = endpoint.description || endpoint.summary;
  const fileUpload = endpoint.bodyFormat === 'multipart';
  return (
    <li className={`ai-review-ep ai-review-ep--${kind}`}>
      <button type="button" className="ai-review-ep__head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="ai-review-ep__chevron" aria-hidden="true">{open ? <FiChevronDown size={14}/> : <FiChevronRight size={14}/>}</span>
        <span className={methodClass(endpoint.httpMethod || 'get')}>{(endpoint.httpMethod || '').toUpperCase()}</span>
        <span className="ai-review-ep__title">
          <span className="ai-review-ep__name">{endpoint.name}</span>
          <span className="ai-mono ai-review-ep__path">{endpoint.pathTemplate}</span>
        </span>
        {kind !== 'read' && <span className={`ai-review-tag ai-review-tag--${kind}`}>{kind === 'delete' ? 'Can delete data' : 'Can change data'}</span>}
        {fileUpload && <span className="ai-review-tag">File upload</span>}
      </button>
      <div className="ai-review-ep__body">
        {description
          ? <p className="ai-review-ep__desc">{description}</p>
          : <p className="ai-review-ep__desc ai-warn-text">No description - the agent won't know when to use this.</p>}
        <div className="ai-review-ep__facts">
          <span><span className="ai-review-ep__label">Sends</span>{' '}
            {params.length
              ? params.map((p, i) => <React.Fragment key={i}>{i > 0 && ', '}<span className="ai-mono">{p.name}{p.required ? '*' : ''}</span></React.Fragment>)
              : <span className="ai-review-ep__muted">nothing</span>}
          </span>
          {endpoint.returnsSummary && (
            <span><span className="ai-review-ep__label">Returns</span> <span className="ai-mono">{endpoint.returnsSummary}</span></span>
          )}
          {fileUpload && <span className="ai-warn-text">Not available to agents - they can't send files.</span>}
        </div>
        {open && params.length > 0 && (
          <div className="ai-table-scroll">
            <table className="ai-param-table">
              <thead><tr><th>Param</th><th>Sent in</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
              <tbody>
                {params.map((p, i) => (
                  <tr key={i}>
                    <td className="ai-mono">{p.name}</td>
                    <td>{LOCATION_LABELS[p.location || ''] ?? p.location}</td>
                    <td>{p.type}</td>
                    <td>{p.required ? 'Yes' : ''}</td>
                    <td>
                      {p.description}
                      {p.enumValues?.length ? <span className="ai-mono"> ({p.enumValues.join(', ')})</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </li>
  );
};

const IntegrationReviewDetails: React.FC<{ integration: ReviewIntegration }> = ({ integration }) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const endpoints = useMemo(() => integration.endpoints ?? [], [integration.endpoints]);

  const counts = useMemo(() => {
    const c = { read: 0, change: 0, delete: 0, upload: 0 };
    endpoints.forEach(ep => {
      c[kindOf(ep.httpMethod)] += 1;
      if (ep.bodyFormat === 'multipart') c.upload += 1;
    });
    return c;
  }, [endpoints]);

  const q = search.trim().toLowerCase();
  const visible = endpoints.filter(ep => (filter === 'all' || kindOf(ep.httpMethod) === filter)
    && (!q || [ep.name, ep.pathTemplate, ep.description, ep.summary].some(v => (v || '').toLowerCase().includes(q))));
  const shown = showAll || q || filter !== 'all' ? visible : visible.slice(0, FOLDED);

  const auth = integration.authType === 'bearer' ? 'Bearer token'
    : integration.authType === 'api_key'
      ? `API key in the ${integration.authLocation === 'query' ? 'query string' : 'header'}${integration.authParamName ? ` (${integration.authParamName})` : ''}`
      : 'None';
  const keys = integration.authType === 'none' || !integration.authType ? 'Not needed'
    : integration.installerKeyScope === 'per_user' ? 'Each user at another agency enters their own'
      : "Each agency's Agency Admin enters one for their agency";

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: endpoints.length },
    { key: 'read', label: 'Read only', count: counts.read },
    { key: 'change', label: 'Can change data', count: counts.change },
    { key: 'delete', label: 'Can delete data', count: counts.delete },
  ];

  return (
    <div className="ai-review">
      <section className="ai-review-card">
        <div className="ai-review-card__title">What it calls</div>
        <dl className="ai-review-grid">
          <dt>Host</dt><dd className="ai-mono">{hostOf(integration.baseUrl) || 'Not set'}</dd>
          <dt>Base URL</dt><dd className="ai-mono">{integration.baseUrl || 'Not set'}</dd>
          <dt>Authentication</dt><dd>{auth}</dd>
          <dt>Other agencies' API key</dt><dd>{keys}</dd>
        </dl>
      </section>

      <section className="ai-review-card">
        <div className="ai-review-card__title">What it can do <span className="ai-section-count">{endpoints.length} endpoints</span></div>
        <div className="ai-review-summary">
          <span className="ai-review-stat">{counts.read} read only</span>
          <span className={`ai-review-stat${counts.change ? ' ai-review-stat--change' : ''}`}>{counts.change} can change data</span>
          <span className={`ai-review-stat${counts.delete ? ' ai-review-stat--delete' : ''}`}>{counts.delete} can delete data</span>
          {counts.upload > 0 && <span className="ai-review-stat">{counts.upload} file upload{counts.upload === 1 ? '' : 's'} (not for agents)</span>}
        </div>
        <p className="ai-review-hint">
          Agents choose an endpoint by its description and fill in what it sends. Check that the descriptions are clear,
          and that anything able to change or delete data (marked by its method: POST, PUT, PATCH or DELETE) is
          something agencies should allow. Open an endpoint to see each thing it sends.
        </p>

        {endpoints.length > 0 && (<>
          <div className="ai-review-toolbar">
            <div className="ai-review-search">
              <FiSearch size={12} aria-hidden="true"/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search endpoints…" aria-label="Search endpoints"/>
            </div>
            <div className="ai-review-filters" role="group" aria-label="Filter endpoints">
              {filters.map(f => (
                <button key={f.key} type="button" disabled={f.key !== 'all' && f.count === 0}
                  className={`ai-review-filter${filter === f.key ? ' ai-review-filter--on' : ''}`}
                  onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}>
                  {f.label} <span>{f.count}</span>
                </button>
              ))}
            </div>
          </div>
          {shown.length === 0
            ? <p className="ai-review-hint">No endpoints match.</p>
            : <ul className="ai-review-list">{shown.map((ep, i) => <EndpointRow key={`${ep.name}-${i}`} endpoint={ep}/>)}</ul>}
          {!showAll && !q && filter === 'all' && visible.length > FOLDED && (
            <button type="button" className="ai-toggle-link" onClick={() => setShowAll(true)}>Show all {visible.length} endpoints</button>
          )}
        </>)}
      </section>
    </div>
  );
};

export default IntegrationReviewDetails;
